import { useEffect, useState, type ReactNode } from 'react';
import {
    getTaskCommits,
    type AuthChannel, type TaskComment, type TaskCommit, type User,
} from '../../../packages/api-client/pm-api';
import { Bot, GitCommitHorizontal, ExternalLink, Compass, Brain } from 'lucide-react';

// ─── Attribution ─────────────────────────────────────────────────────────────

export function displayName(u?: { first_name?: string; last_name?: string; email?: string } | null) {
    if (!u) return '';
    return `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || '';
}

/**
 * How a write was authenticated, from the server-recorded auth_channel (never
 * client input). `who` is the authenticated owner; `impersonator` is the
 * resolved name of auth_impersonated_by, or '' when that user isn't listed.
 */
export function attributionLabel(channel: AuthChannel | undefined, who: string, impersonatedBy?: string, impersonator?: string) {
    const base = channel === 'api_key' ? `${who} via API key`
        : channel === 'oauth_connector' ? `${who} via Claude connector (OAuth)`
            : channel === 'assistant' ? `${who} via assistant`
                : channel === 'human' ? `by ${who}`
                    : `by ${who} (channel not recorded)`;
    if (!impersonatedBy) return base;
    return `${base} · impersonated by ${impersonator || 'a platform admin'}`;
}

type UserMap = Record<string, User>;

function commentAttribution(c: TaskComment, users: UserMap) {
    const who = displayName({ first_name: c.author_first_name, last_name: c.author_last_name, email: c.author_email }) || 'Unknown user';
    return attributionLabel(c.auth_channel, who, c.auth_impersonated_by, displayName(users[c.auth_impersonated_by ?? '']));
}

function latest(comments: TaskComment[], kind: TaskComment['kind']) {
    return comments
        .filter(c => c.kind === kind)
        .reduce<TaskComment | null>((a, c) => (!a || c.created_at >= a.created_at ? c : a), null);
}

function HandoffEntry({ c, users, icon, label }: { c: TaskComment; users: UserMap; icon: ReactNode; label: string }) {
    return (
        <article className="task-handoff-entry">
            <div className="task-meta-row">
                <span className="task-chip">{icon} {label}</span>
                <span>{commentAttribution(c, users)}</span>
                {c.author_agent && <span className="task-chip"><Bot size={13} aria-hidden /> {`${c.author_agent} · self-reported`}</span>}
                <time dateTime={c.created_at}>{new Date(c.created_at).toLocaleString()}</time>
            </div>
            <p className="task-handoff-body">{c.body}</p>
        </article>
    );
}

// ─── Agent handoff (§5) ──────────────────────────────────────────────────────

export function AgentHandoff({ taskId, comments, commentsError, users, showCodeEvidence = true }: {
    showCodeEvidence?: boolean;
    taskId: string;
    comments: TaskComment[];
    commentsError: boolean;
    users: User[];
}) {
    const userMap: UserMap = Object.fromEntries(users.map(u => [u.id, u]));
    const summary = latest(comments, 'llm_summary');
    const reasoning = latest(comments, 'llm_reasoning');

    return (
        <>
            {commentsError ? (
                <p role="alert" className="task-state-error">The handoff summary couldn't be loaded.</p>
            ) : summary ? (
                <>
                    <HandoffEntry c={summary} users={userMap} icon={<Compass size={13} aria-hidden />} label="Latest summary" />
                    {reasoning && <HandoffEntry c={reasoning} users={userMap} icon={<Brain size={13} aria-hidden />} label="Reasoning" />}
                </>
            ) : (
                <p className="task-muted">No agent summary on this task yet.</p>
            )}
            {showCodeEvidence && <>
                <h3 className="task-subheading">Linked evidence</h3>
                <CommitsSection taskId={taskId} users={userMap} />
            </>}
        </>
    );
}

// ─── Linked commits ──────────────────────────────────────────────────────────

function commitHref(c: TaskCommit): string | null {
    if (!c.repo_url) return null;
    const base = c.repo_url.replace(/\.git$/, '').replace(/\/$/, '');
    if (base.includes('github.com') || base.includes('gitlab.com') || base.includes('bitbucket.org')) {
        return `${base}/commit/${c.sha}`;
    }
    return null;
}

export function CommitsSection({ taskId, users = {} }: { taskId: string; users?: UserMap }) {
    const [commits, setCommits] = useState<TaskCommit[]>([]);
    const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');

    useEffect(() => {
        setState('loading');
        getTaskCommits(taskId)
            .then(cs => { setCommits(cs); setState('ok'); })
            .catch(() => setState('error'));
    }, [taskId]);

    if (state === 'loading') return <p className="task-muted" role="status">Loading linked commits…</p>;
    if (state === 'error') return <p role="alert" className="task-state-error">Linked commits couldn't be loaded.</p>;
    if (commits.length === 0) return <p className="task-muted">No linked commits or PRs.</p>;

    return (
        <div>
            <p className="task-muted task-inline"><GitCommitHorizontal size={16} aria-hidden /> {commits.length} linked {commits.length === 1 ? 'commit' : 'commits'}</p>
            <ul className="task-list">
                {commits.map(c => {
                    const href = commitHref(c);
                    const shortSHA = c.sha.length > 7 ? c.sha.slice(0, 7) : c.sha;
                    const who = displayName(users[c.author_id ?? '']) || 'a workspace member';
                    return (
                        <li key={c.id} className="task-list-row task-commit">
                            <code className="task-sha" title={c.sha}>
                                {href ? <a href={href} target="_blank" rel="noreferrer">{shortSHA}</a> : shortSHA}
                            </code>
                            <div className="task-grow">
                                <div>{c.message || <span className="task-muted">(no message)</span>}</div>
                                <div className="task-meta-row">
                                    {/* Link provenance stays separate from authenticated delivery evidence. */}
                                    <span className="task-chip task-chip--warn">Self-reported link</span>
                                    {c.branch && <span>{c.branch}</span>}
                                    <time dateTime={c.created_at}>{new Date(c.created_at).toLocaleString()}</time>
                                    <span>{attributionLabel(c.auth_channel, who, c.auth_impersonated_by, displayName(users[c.auth_impersonated_by ?? '']))}</span>
                                    {c.author_agent && <span><Bot size={13} aria-hidden /> {`${c.author_agent} · self-reported`}</span>}
                                    {c.pr_url && (
                                        <a href={c.pr_url} target="_blank" rel="noreferrer"><ExternalLink size={13} aria-hidden /> Linked PR</a>
                                    )}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
